import { FormEvent, useEffect, useMemo, useState } from 'react'
import { MANUAL_PRESETS, MACHINE_CATALOG, type MachineExercise } from '../lib/machines'
import type { MuscleId } from '../lib/muscles'
import type { WorkoutRecord } from '../lib/db'
import { isSameExercise } from '../lib/db'
import {
  intensityFromHistory,
  sessionLoad,
  suggestSets,
  type WorkoutSet,
} from '../lib/volume'
import { SearchableSelect, type SearchableSelectOption } from './SearchableSelect'

export interface WorkoutDraft {
  name: string
  source: 'qr' | 'manual'
  machineId?: string
  qrPayload?: string
  muscles: MuscleId[]
  sets: WorkoutSet[]
  intensity: number
  notes?: string
}

interface Props {
  open: boolean
  initial?: Partial<WorkoutDraft> & { machine?: MachineExercise }
  /** Full history - used to suggest weights and compute relative intensity. */
  history?: WorkoutRecord[]
  onClose: () => void
  onSave: (draft: WorkoutDraft) => void
}

const EMPTY_HISTORY: WorkoutRecord[] = []

const FOCUS_OPTIONS: SearchableSelectOption[] = [
  ...MANUAL_PRESETS.map((p) => ({ id: p.id, label: p.name, group: 'Manual' })),
  ...MACHINE_CATALOG.map((m) => ({ id: m.id, label: m.name, group: 'Machines' })),
]

function buildStarterSets(
  initial: Props['initial'],
  history: WorkoutRecord[],
  name: string,
  machineId?: string,
): WorkoutSet[] {
  if (initial?.sets?.length) return initial.sets.map((s) => ({ ...s }))

  const match = history.find((w) => isSameExercise(w, { name, machineId }))
  const setCount = initial?.machine?.defaultSets ?? 3
  const defaultReps = initial?.machine?.defaultReps ?? 12
  return suggestSets(match?.sets, { setCount, defaultReps, defaultWeight: 10 })
}

export function AddWorkoutModal({ open, initial, history = EMPTY_HISTORY, onClose, onSave }: Props) {
  const [name, setName] = useState(() => initial?.name ?? initial?.machine?.name ?? MANUAL_PRESETS[0].name)
  const [presetId, setPresetId] = useState(() => initial?.machineId ?? MANUAL_PRESETS[0].id)
  const [setRows, setSetRows] = useState<WorkoutSet[]>(() =>
    buildStarterSets(initial, history, initial?.name ?? initial?.machine?.name ?? MANUAL_PRESETS[0].name, initial?.machineId),
  )
  const [notes, setNotes] = useState(() => initial?.notes ?? '')

  useEffect(() => {
    if (!open) return
    const nextName = initial?.name ?? initial?.machine?.name ?? MANUAL_PRESETS[0].name
    const nextMachineId = initial?.machineId ?? initial?.machine?.id
    setName(nextName)
    setPresetId(nextMachineId ?? MANUAL_PRESETS[0].id)
    setSetRows(buildStarterSets(initial, history, nextName, nextMachineId))
    setNotes(initial?.notes ?? '')
  }, [open, initial, history])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const machineIdForHistory = initial?.machine?.id ?? (MACHINE_CATALOG.find((m) => m.id === presetId)?.id)
  const priorLoads = useMemo(() => {
    const exercise = { name, machineId: machineIdForHistory }
    return history.filter((w) => isSameExercise(w, exercise)).map((w) => w.sets)
  }, [history, name, machineIdForHistory])

  const liveIntensity = intensityFromHistory(setRows, priorLoads)
  const liveLoad = sessionLoad(setRows)

  if (!open) return null

  const updateRow = (index: number, patch: Partial<WorkoutSet>) => {
    setSetRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const addRow = () => {
    setSetRows((rows) => {
      const last = rows[rows.length - 1]
      return [...rows, { reps: last.reps, weight: last.weight }]
    })
  }

  const removeRow = (index: number) => {
    setSetRows((rows) => rows.filter((_, i) => i !== index || rows.length <= 1))
  }

  const applyFocus = (id: string) => {
    setPresetId(id)
    const p = MANUAL_PRESETS.find((x) => x.id === id)
    const m = MACHINE_CATALOG.find((x) => x.id === id)
    const selected = p ?? m
    if (!selected) return
    setName(selected.name)
    const match = history.find((w) =>
      isSameExercise(w, { name: selected.name, machineId: m?.id ?? p?.id }),
    )
    setSetRows(
      suggestSets(match?.sets, {
        setCount: m?.defaultSets ?? 3,
        defaultReps: m?.defaultReps ?? 12,
        defaultWeight: 10,
      }),
    )
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const machine = initial?.machine ?? MACHINE_CATALOG.find((m) => m.id === presetId)
    const preset = MANUAL_PRESETS.find((p) => p.id === presetId)
    const muscles = initial?.muscles ?? machine?.muscles ?? preset?.muscles ?? []
    const resolvedName = name.trim() || machine?.name || preset?.name || 'Workout'
    const machineId = machine?.id ?? (preset ? preset.id : undefined)
    const cleaned = setRows.map((s) => ({
      reps: Math.max(1, Number(s.reps)),
      weight: Math.max(0, Number(s.weight)),
    }))
    const prior = history
      .filter((w) => isSameExercise(w, { name: resolvedName, machineId }))
      .map((w) => w.sets)
    onSave({
      name: resolvedName,
      source: initial?.source ?? (machine ? 'qr' : 'manual'),
      machineId,
      qrPayload: initial?.qrPayload ?? machine?.qrPayload,
      muscles,
      sets: cleaned,
      intensity: intensityFromHistory(cleaned, prior),
      notes: notes.trim() || undefined,
    })
    onClose()
  }

  const lockedMachine = Boolean(initial?.machine)

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <h2 id="add-title">{lockedMachine ? 'Log scanned workout' : 'Add workout'}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            X
          </button>
        </header>

        <form className="stack" onSubmit={submit}>
          <label htmlFor="wo-name">
            Name
            <input id="wo-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>

          {!lockedMachine && (
            <SearchableSelect
              id="wo-preset"
              label="Focus"
              value={presetId}
              options={FOCUS_OPTIONS}
              onChange={applyFocus}
              placeholder="Search focus..."
            />
          )}

          <fieldset className="set-log">
            <legend>Sets</legend>
            <div className="set-log-head" aria-hidden="true">
              <span>#</span>
              <span>Weight</span>
              <span>Reps</span>
              <span />
            </div>
            <ul className="set-log-rows">
              {setRows.map((row, index) => (
                <li key={index} className="set-log-row">
                  <span className="set-num" aria-hidden="true">
                    {index + 1}
                  </span>
                  <label className="sr-only" htmlFor={`wo-weight-${index}`}>
                    Weight set {index + 1}
                  </label>
                  <input
                    id={`wo-weight-${index}`}
                    type="number"
                    min={0}
                    step={0.5}
                    value={row.weight}
                    onChange={(e) => updateRow(index, { weight: Number(e.target.value) })}
                    required
                  />
                  <label className="sr-only" htmlFor={`wo-reps-${index}`}>
                    Reps set {index + 1}
                  </label>
                  <input
                    id={`wo-reps-${index}`}
                    type="number"
                    min={1}
                    max={100}
                    value={row.reps}
                    onChange={(e) => updateRow(index, { reps: Number(e.target.value) })}
                    required
                  />
                  <button
                    type="button"
                    className="linkish danger"
                    onClick={() => removeRow(index)}
                    disabled={setRows.length <= 1}
                    aria-label={`Remove set ${index + 1}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="btn-ghost set-add" onClick={addRow}>
              Add set
            </button>
            <p className="muted small intensity-hint" role="status">
              Volume {liveLoad.toLocaleString()} · intensity {liveIntensity}/10
              {priorLoads.length === 0
                ? ' (first run for this exercise)'
                : ' (vs your best prior session)'}
            </p>
          </fieldset>

          <label htmlFor="wo-notes">
            Notes (optional)
            <textarea id="wo-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          <button type="submit" className="btn-primary">
            Save workout
          </button>
        </form>
      </div>
    </div>
  )
}
